import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import type { IndustryStep } from "@/data/industriesData";
import { Button } from "@/components/ui/button";

interface Props {
  steps: IndustryStep[];
  color?: string;
  sectionTitle?: string;
  onCtaClick?: () => void;
}

const IndustrySteps = ({ steps, color, sectionTitle = "Your journey, step by step", onCtaClick }: Props) => {
  const accent = "var(--primary)";

  if (steps.length === 0) return null;

  return (
    <section id="service-offerings" className="py-14 sm:py-20">
      <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-6 h-0.5 rounded-full bg-primary" />
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Our expertise</p>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-foreground">{sectionTitle}</h2>
        </div>

        <Accordion type="single" collapsible defaultValue="step-0" className="space-y-3">
          {steps.map((step, i) => (
            <AccordionItem key={i} value={`step-${i}`} className="border rounded-xl px-5" style={{ borderColor: `${accent}25` }}>
              <AccordionTrigger className="text-base font-bold text-foreground hover:no-underline gap-3">
                <div className="flex items-center gap-3">
                  <span
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold text-white shrink-0"
                    style={{ backgroundColor: accent }}
                  >
                    {i + 1}
                  </span>
                  {step.title}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
                  {step.items.map((item, j) => (
                    <motion.div
                      key={j}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: j * 0.04 }}
                      className="flex items-center gap-2.5 px-4 py-3 rounded-lg text-sm text-foreground"
                    >
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: accent }}
                      />
                      {item}
                    </motion.div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {onCtaClick && (
          <div className="mt-8 flex justify-center">
            <Button
              size="lg"
              onClick={onCtaClick}
              className="gap-2 text-white rounded-full"
              style={{ backgroundColor: accent }}
            >
              Get started
              <ArrowRight size={16} />
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default IndustrySteps;
